import React, { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../../../../store/hooks";
import Select from "react-select";
import {
  TextEditorSelectStyle,
  DefaultReactSelectStyle,
} from "../../../ReactSelectStyle/ReactSelectStyle";
import {
  Template,
  TEMPLATE_USE_GENERAL,
} from "../../../../../store/reducers/builderReducers/templates/templateApi";
import {
  findTemplateByStoredReference,
  getTemplateSourceComponent,
} from "../../../../../store/reducers/builderReducers/templates/templateHelpers";
import {
  updateStructureComponent,
} from "../../../../../store/reducers/builderReducers/structure/structureActions";
import ColorPicker from "../FontMenu/ColorPicker/ColorPicker";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import StructureAPI from "../../../../../store/reducers/builderReducers/structure/structureApi";
import { AccordionComponent } from "../../../../_default/interfaces/base";
import {
  updateActiveEditor,
} from "../../../../../store/reducers/builderReducers/editor/editorActions";
import Spinner from "../../Spinner/Spinner";
import { Edit } from "@mui/icons-material";
import {
  getComponentFromStructureMap,
  getRelationChildKey,
} from "../../../../_default/utilities/componentIdentity";
import { getComponentType } from "../../../../../utils/Helpers";

export default function AccordionMenu({ id, triggerResize }) {
  const component = useAppSelector(
    (state) => getComponentFromStructureMap(state.builder.structure.components, id)
  ) as AccordionComponent;

  const components = useAppSelector(
    (state) => state.builder.structure.components
  );

  const [selectedTemplateId, setSelectedTemplateId] = useState(
    component.attributes.template_id
  );

  const editor = useAppSelector((state) => state.builder.editor);
  const [templateLoading, setTemplateLoading] = useState(false);

  useEffect(() => {
    setSelectedTemplateId(component.attributes.template_id);
  }, [component]);

  const [isLoading, setIsLoading] = useState(false);

  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  const allTemplates = useAppSelector((state) => state.builder.templates);

  const templateOptions = useMemo(() => {
    const templatesList: Template[] =
      allTemplates &&
      typeof allTemplates === "object" &&
      typeof (allTemplates as { toArray?: () => Template[] }).toArray === "function"
        ? (allTemplates as { toArray: () => Template[] }).toArray()
        : Array.isArray(allTemplates)
        ? (allTemplates as Template[])
        : [];

    const allowedUseValues = new Set([
      TEMPLATE_USE_GENERAL.toLowerCase(),
      "checkoutterms",
      "cookiepolicy",
    ]);
    return templatesList
      .map((template) => {
        const source = getTemplateSourceComponent(template);
        if (!source) {
          return null;
        }
        const normalizedUse = String(template.use ?? "").trim().toLowerCase();
        if (!allowedUseValues.has(normalizedUse)) {
          return null;
        }
        const rootType = getComponentType(source);
        if (!["row", "col"].includes(rootType)) {
          return null;
        }
        const rootRef =
          template.rootComponentUuid?.trim() ||
          source.uuid?.trim() ||
          "";
        if (!rootRef) {
          return null;
        }
        return {
          value: rootRef,
          label: template.name,
          id: rootRef,
          template,
        };
      })
      .filter((option): option is {
        value: string;
        label: string;
        id: string;
        template: Template;
      } => !!option)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allTemplates]);

  const iconPositionOptions = [
    { value: "left", label: "Left" },
    { value: "right", label: "Right" },
  ];

  const contentAlginOptions = [
    { value: "start", label: "Left" },
    { value: "center", label: "Center" },
    { value: "end", label: "Right" },
  ];

  const widthTypes = [{ value: "px", label: "px" }];

  const handleComponentChange = async (option: any) => {
    const nextTemplateRef = option?.id;
    if (!nextTemplateRef || String(nextTemplateRef) === String(selectedTemplateId)) {
      return;
    }
    const template = option?.template as Template | undefined;
    const source = getTemplateSourceComponent(template);
    if (!source) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const webpageRef =
        typeof component?.webpage_uuid === "string" && component.webpage_uuid.trim() !== ""
          ? component.webpage_uuid.trim()
          : component?.webpage_id;

      if (webpageRef == null || webpageRef === "") {
        updateStructureComponent(id, "attributes.template_id", nextTemplateRef);
        setSelectedTemplateId(nextTemplateRef);
        return;
      }

      const existingContentRelation = (
        component.childWebpageComponentRelations ?? []
      ).find((relation) => relation?.sort === 2);
      const existingContentComponent = existingContentRelation
        ? getComponentFromStructureMap(
            components,
            getRelationChildKey(existingContentRelation) ??
              existingContentRelation.child_id
          )
        : null;

      if (existingContentComponent) {
        await StructureAPI.deleteComponent(webpageRef, existingContentComponent as any);
      }

      const injectResult = await StructureAPI.injectTemplateComponent(webpageRef, source, {
        parent_id: component.id,
        parent_uuid: component.uuid ?? null,
        sort_order: 2,
      });
      if (!injectResult?.injectedRoot) {
        updateStructureComponent(id, "attributes.template_id", nextTemplateRef);
        setSelectedTemplateId(nextTemplateRef);
        return;
      }
      // Keep stored selector reference pointing to the actual template row/root,
      // not the injected clone UUID (which is unique per insertion and not in template list).
      updateStructureComponent(id, "attributes.template_id", nextTemplateRef);
      setSelectedTemplateId(nextTemplateRef);
    } finally {
      setIsLoading(false);
    }
  };

  async function setTemplateActive(e: any, template_: Template) {
    e.stopPropagation();
    setTemplateLoading(true);
    const source = getTemplateSourceComponent(template_);
    if (!source) {
      setTemplateLoading(false);
      return;
    }
    updateActiveEditor({
      ...editor,
      component_uuid: source.uuid?.trim() || null,
      component_id: source.id ?? null,
      component_parent_uuid: null,
      component_parent_id: null,
      template: source,
    });
    setTemplateLoading(false);
  }

  useEffect(() => {
    triggerResize();
  }, []);

  return (
    <div className="" style={{ display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          gap: "3px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div className="b-mx-1">Content template:</div>

        {isLoading && (
          <div className="spinner-border spinner-border-sm" role="status">
            {/* Add loading icon */}
            <span className="sr-only"></span>
          </div>
        )}

        <Select
          onChange={(e) => {
            handleComponentChange(e);
          }}
          isSearchable={false}
          className="m-0 link-menu_input"
          styles={{
            ...TextEditorSelectStyle,
            container: (base) => ({
              ...base,
              display: "flex",

              width: "120px",
              minWidth: "60px",
            }),
            option: (styles, data) => ({
              ...DefaultReactSelectStyle.option!(styles, data),
              display: "flex",
              justifyContent: "space-between",
            }),

            control: (base) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }),
          }}
          noOptionsMessage={({}) => ""}
          value={
            templateOptions.find((x) => String(x.id) === String(selectedTemplateId)) ??
            null
          }
          options={[...templateOptions]}
          placeholder="Select..."
        ></Select>

        {selectedTemplateId && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              textTransform: "uppercase",
            }}
            onClick={(e) => {
              if (selectedTemplateId) {
                const templateComponent = findTemplateByStoredReference(
                  allTemplates,
                  selectedTemplateId
                );

                if (!templateComponent) return;

                setTemplateActive(e, templateComponent);
              }
            }}
          >
            {templateLoading ? (
              <Spinner style={{ fontSize: "14px" }} as="div" />
            ) : (
              <Edit style={{ fontSize: "14px" }} />
            )}
          </div>
        )}

        <div className="b-mx-1">Default expanded:</div>

        <label className="switch" style={{ marginRight: "5px" }}>
          <input
            type="checkbox"
            checked={
              component.attributes?.default_expanded
                ? component.attributes.default_expanded
                : false
            }
            onChange={(e) => {
              updateStructureComponent(
                id,
                "attributes.default_expanded",
                e.target.checked
              );

              // updateStructureComponent(
              //   id,
              //   "attributes.backgroundVideo.autoplay",
              //   e.target.checked
              // );
            }}
          />
          <span className="slider round"></span>
        </label>

        <div className="b-mx-1">Align content:</div>

        <Select
          onChange={(e) => {
            updateStructureComponent(id, "attributes.align_content", e!.value);
          }}
          isSearchable={false}
          className="m-0 link-menu_input"
          styles={{
            ...TextEditorSelectStyle,
            container: (base) => ({
              ...base,
              display: "flex",

              width: "auto",
              minWidth: "30px",
            }),
            option: (styles, data) => ({
              ...DefaultReactSelectStyle.option!(styles, data),
              display: "flex",
              justifyContent: "space-between",
            }),

            control: (base) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }),
          }}
          noOptionsMessage={({}) => ""}
          value={
            contentAlginOptions.find(
              (pos) => pos.value === component.attributes?.align_content
            )
              ? contentAlginOptions.find(
                  (pos) => pos.value === component.attributes?.align_content
                )
              : contentAlginOptions[0]
          }
          options={[...contentAlginOptions]}
          placeholder=""
        ></Select>

        <div className="b-mx-1">Icon pos.:</div>

        <Select
          onChange={(e) => {
            updateStructureComponent(id, "attributes.icon_position", e!.value);
          }}
          isSearchable={false}
          className="m-0 link-menu_input"
          styles={{
            ...TextEditorSelectStyle,
            container: (base) => ({
              ...base,
              display: "flex",

              width: "auto",
              minWidth: "30px",
            }),
            option: (styles, data) => ({
              ...DefaultReactSelectStyle.option!(styles, data),
              display: "flex",
              justifyContent: "space-between",
            }),

            control: (base) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }),
          }}
          noOptionsMessage={({}) => ""}
          value={
            iconPositionOptions.find(
              (pos) => pos.value === component.attributes?.icon_position
            )
              ? iconPositionOptions.find(
                  (pos) => pos.value === component.attributes?.icon_position
                )
              : iconPositionOptions[1]
          }
          options={[...iconPositionOptions]}
          placeholder=""
        ></Select>

        <div className="b-mx-1">Icon size:</div>

        <div style={{ display: "flex", gap: "5px" }}>
          <input
            type="number"
            className="global-editor__input builder-input spacing-menu_input"
            size={1}
            value={
              component.attributes?.icon_size
                ? component.attributes.icon_size
                : 18
            }
            onChange={(ev) => {
              if (ev.target.value === "0") return;

              updateStructureComponent(
                id,
                "attributes.icon_size",
                ev.target.value
              );
            }}
          />

          <Select
            menuPosition="fixed"
            className="m-0 button-menu_input"
            isSearchable={false}
            styles={TextEditorSelectStyle}
            noOptionsMessage={({}) => ""}
            value={widthTypes.find((x) => x.value === "px")}
            onChange={(val) => {
              // setSelectedWidth(val.value);
              // updateComponentStyle(id, {
              //   width: component.attributes.styles.width
              //     ? component.attributes.styles.width.replace(
              //         `${selectedWidth}`,
              //         val.value
              //       )
              //     : "",
              // });
            }}
            options={widthTypes}
            placeholder=""
          ></Select>
        </div>

        <div className="b-mx-1">Icon color:</div>

        <div>
          <FormatColorTextIcon
            style={{
              cursor: "pointer",
              fontSize: "18px",
              color: component.attributes.icon_color
                ? component.attributes.icon_color
                : "#fff",
            }}
            onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
          />

          {isColorPickerOpen && (
            <ColorPicker
              title="Expand icon color"
              onChange={(val) => {
                updateStructureComponent(id, "attributes.icon_color", val);

                // sfdcksjdfklj
              }}
              value={
                component.attributes.icon_color
                  ? component.attributes.icon_color
                  : "#000"
              }
              setColorPickerOpen={setIsColorPickerOpen}
            />
          )}
        </div>
      </div>
    </div>
  );
}
